if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.isSubmitting = false; // Add submission flag

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.productVariantIds = this.dataset.productVariantIds ? this.dataset.productVariantIds.split(',') : [];
      }

      connectedCallback() {
        if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate.bind(this));
        }
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }

      handleCartUpdate(event) {
        const cartData = event.cartData;
        if (!cartData || !cartData.items) return;

        // Check if any variant of this product is in the cart
        const productInCart = cartData.items.some(item =>
          this.productVariantIds.includes(item.variant_id.toString())
        );

        this.updateButtonState(productInCart);
      }

      updateButtonState(productInCart) {
        if (productInCart) {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButton.removeAttribute('aria-disabled');
          this.variantIdInput.disabled = true;
          this.submitButtonText.textContent = window.variantStrings?.alreadyInCart || 'Already in Cart';
        } else {
          // Only enable if button text indicates it should be enabled
          const currentText = this.submitButtonText.textContent;
          const isSoldOut = currentText === (window.variantStrings?.soldOut || 'Sold out');
          const isUnavailable = currentText === (window.variantStrings?.unavailable || 'Unavailable');

          if (!isSoldOut && !isUnavailable) {
            this.submitButton.removeAttribute('disabled');
            this.submitButton.removeAttribute('aria-disabled');
            this.variantIdInput.disabled = false;
            this.submitButtonText.textContent = window.variantStrings?.addToCart || 'Add to cart';
          }
        }
      }

      checkCartStatus() {
        // Check if any variant of this product is in the cart
        return fetch(window.Shopify.routes.root + 'cart.js')
          .then(response => response.json())
          .then(cart => {
            const productInCart = cart.items.some(item =>
              this.productVariantIds.includes(item.variant_id.toString())
            );
            this.updateButtonState(productInCart);
          })
          .catch(err => console.error('Error checking cart status:', err));
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        // Prevent duplicate submissions
        if (this.isSubmitting) {
          console.warn('Form submission already in progress');
          return;
        }
        this.isSubmitting = true;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => {
            if (!response.ok) {
              if (response.status === 429) {
                throw new Error('Too many requests. Please wait a moment and try again.');
              }
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            return response.json();
          })
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage(e.message || 'An error occurred. Please try again.');
          })
          .finally(() => {
            this.isSubmitting = false; // Reset submission flag
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);

            // Don't remove aria-disabled here - let the cart update handler manage button state
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        // Always check cart status when toggling button
        fetch(window.Shopify.routes.root + 'cart.js')
          .then(response => response.json())
          .then(cart => {
            const productInCart = cart.items.some(item =>
              this.productVariantIds.includes(item.variant_id.toString())
            );

            if (productInCart) {
              // Product is in cart - always show "Already in Cart"
              this.submitButton.setAttribute('disabled', 'disabled');
              this.submitButton.removeAttribute('aria-disabled');
              this.variantIdInput.disabled = true;
              this.submitButtonText.textContent = window.variantStrings?.alreadyInCart || 'Already in Cart';
            } else if (disable) {
              // Not in cart but disabled for other reasons (sold out/unavailable)
              this.submitButton.setAttribute('disabled', 'disabled');
              if (text) this.submitButtonText.textContent = text;
            } else {
              // Not in cart and available
              this.submitButton.removeAttribute('disabled');
              this.submitButton.removeAttribute('aria-disabled');
              this.variantIdInput.disabled = false;
              this.submitButtonText.textContent = window.variantStrings?.addToCart || 'Add to cart';
            }
          })
          .catch(() => {
            // Fallback if cart fetch fails
            if (disable) {
              this.submitButton.setAttribute('disabled', 'disabled');
              if (text) this.submitButtonText.textContent = text;
            } else {
              this.submitButton.removeAttribute('disabled');
              this.submitButtonText.textContent = window.variantStrings?.addToCart || 'Add to cart';
            }
          });
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
